/**
 * Whether the server can be reached right now, and the hook api.ts uses to hand a capture to the offline queue.
 *
 * `navigator.onLine` only says a network interface is up, not that this server answers, so the real signal is
 * the outcome of the last request: any request that gets no answer marks the server unreachable, any answer
 * marks it reachable. Lives apart from api.ts and engine.ts because api.ts calls it and engine.ts calls api.ts.
 */
import { useSyncExternalStore } from "react";

type Queue = (commandType: string, batchId: string, body: Record<string, unknown>) => Promise<unknown>;

let reachable = typeof navigator === "undefined" ? true : navigator.onLine;
const listeners = new Set<() => void>();
let queueHandler: Queue | null = null;
type ReplicaReader = (path: string) => unknown;
let replicaReader: ReplicaReader | null = null;

const emit = () => {
  for (const l of listeners) l();
};

export function setReachable(value: boolean): void {
  if (reachable === value) return;
  reachable = value;
  emit();
}

export const isReachable = (): boolean => reachable;
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

if (typeof window !== "undefined") {
  window.addEventListener("online", () => setReachable(true));
  window.addEventListener("offline", () => setReachable(false));
}

export const useReachable = (): boolean => useSyncExternalStore(subscribe, isReachable, () => true);

/** The offline runtime registers itself here once it exists. */
export function registerOfflineQueue(h: Queue | null): void {
  queueHandler = h;
}

/** URS-8.6-001: the offline runtime offers reads from the device's replica for GETs that get no answer. */
export function registerReplicaReader(h: ReplicaReader | null): void {
  replicaReader = h;
}

/** `undefined` when there is nothing on the device to answer with. */
export function replicaAnswerFor(path: string): unknown {
  return replicaReader?.(path);
}

/** Null when the capture should go to the server as usual. */
export function offlineQueueFor(): Queue | null {
  return reachable ? null : queueHandler;
}
